import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import AuthLayout from "../components/AuthLayout";
import PasswordStrengthPanel from "../components/PasswordStrengthPanel";
import api from "../api/api";
import { sessionManager } from "../utils/sessionManager";
import { evaluatePasswordStrength, PASSWORD_MAX_LENGTH } from "../utils/passwordPolicy";
import { confirmPasswordInputProps } from "../utils/passwordFieldHandlers";

const extrairMensagemErro = (err, fallback) => {
  const detalhes = err?.response?.data?.details;
  if (Array.isArray(detalhes) && detalhes.length > 0) {
    return detalhes[0]?.mensagem || fallback;
  }
  return err?.response?.data?.mensagem || fallback;
};

const PrimeiroAcesso = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const passwordEvaluation = useMemo(
    () => evaluatePasswordStrength(novaSenha),
    [novaSenha]
  );

  const canSubmit =
    senhaAtual &&
    passwordEvaluation.isValid &&
    novaSenha === confirmarSenha &&
    !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!passwordEvaluation.isValid) {
      setError("A nova senha não atende aos requisitos de segurança.");
      setLoading(false);
      return;
    }

    if (novaSenha !== confirmarSenha) {
      setError("As senhas não conferem.");
      setLoading(false);
      return;
    }

    try {
      await api.post("/auth/primeiro-acesso", {
        senha_atual: senhaAtual,
        nova_senha: novaSenha,
        confirmar_senha: confirmarSenha,
      });

      const usuario = sessionManager.obterUsuario();
      if (usuario) {
        const atualizado = { ...usuario, precisa_trocar_senha: false };
        sessionManager.atualizarUsuario(atualizado);
      }

      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(extrairMensagemErro(err, "Não foi possível atualizar a senha."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Primeiro acesso"
      subtitle="Defina uma nova senha segura para continuar"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          mt: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
        }}
      >
        <TextField
          label="Senha atual"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          value={senhaAtual}
          onChange={(e) => setSenhaAtual(e.target.value)}
          fullWidth
          required
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />

        <TextField
          label="Nova senha"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          value={novaSenha}
          onChange={(e) => setNovaSenha(e.target.value)}
          fullWidth
          required
          autoComplete="new-password"
          inputProps={{ maxLength: PASSWORD_MAX_LENGTH }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />

        <PasswordStrengthPanel
          password={novaSenha}
          confirmPassword={confirmarSenha}
        />

        <TextField
          label="Confirmar nova senha"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          value={confirmarSenha}
          onChange={(e) => setConfirmarSenha(e.target.value)}
          fullWidth
          required
          autoComplete="new-password"
          inputProps={confirmPasswordInputProps}
          error={Boolean(confirmarSenha) && novaSenha !== confirmarSenha}
          helperText={
            confirmarSenha && novaSenha !== confirmarSenha
              ? "As senhas não conferem."
              : "Digite a senha novamente (colar não permitido)."
          }
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!canSubmit}
          sx={{
            py: 1.5,
            fontSize: "1rem",
            borderRadius: 2,
            textTransform: "none",
            bgcolor: theme.palette.primary.main,
            "&:hover": {
              bgcolor: theme.palette.primary.dark,
            },
          }}
        >
          {loading ? (
            <CircularProgress size={26} sx={{ color: "#fff" }} />
          ) : (
            "Atualizar senha"
          )}
        </Button>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
          Por segurança, você precisa definir uma senha forte antes do primeiro acesso.
        </Typography>
      </Box>
    </AuthLayout>
  );
};

export default PrimeiroAcesso;
